'use client';

import { OTHER_SPECIALTY_ID } from '@/components/ui/select/clinician-type-select';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { countries } from '@heidi/utils/countries';
import { useEffect, useRef } from 'react';

import type { DictateFormData } from '../dictate-onboarding.types';

/** Kinde email-only signups store the address in `first_name` until profile save. */
function isLikelyEmail(value: string): boolean {
  return value.includes('@');
}

/**
 * Prefill dictate onboarding profile fields from the signed-in user. Only fills
 * empty fields — never overwrites user-entered values (e.g. get-or-create returns
 * while about-you Continue is in flight). Country falls back to timezone via
 * `getInitialDictateFormData()`; language/spelling defaults resolve on the
 * language step only.
 */
export function useDictateOnboardingPrefill(
  formData: DictateFormData,
  setFormData: (patch: Partial<DictateFormData>) => void
) {
  const { data: userData } = useQueryUser();
  // Deliberately unscoped (no country): this resolves the *persisted*
  // specialty_id, which may have been saved under a different country than the
  // prefilled one. The global list is a superset of every scoped list, so the
  // lookup always resolves; if the seeded specialty isn't in the prefilled
  // country's scoped options, the picker still displays it by name — accepted
  // trade-off
  const { data: specialtiesData } = useQuerySpecialties();
  const prefilledFromUserId = useRef<string | null>(null);
  const hasPrefilledSpecialty = useRef(false);

  // Country is seeded from timezone at flow init, so an emptiness guard never
  // lets profile country win. Snapshot the mount-time default: profile overrides
  // it, a post-mount user change is preserved. Known limit: a change-then-revert
  // to the exact default before userData arrives reads as untouched.
  const initialCountryCode = useRef<string | null>(null);
  const hasCapturedInitialCountry = useRef(false);
  if (!hasCapturedInitialCountry.current) {
    hasCapturedInitialCountry.current = true;
    initialCountryCode.current = formData.country?.countryCode ?? null;
  }

  useEffect(() => {
    if (!userData?.kinde_user_id) return;

    const patch: Partial<DictateFormData> = {};

    if (prefilledFromUserId.current !== userData.kinde_user_id) {
      prefilledFromUserId.current = userData.kinde_user_id;

      if (
        userData.first_name &&
        !formData.firstName.trim() &&
        !isLikelyEmail(userData.first_name)
      ) {
        patch.firstName = userData.first_name;
      }

      if (userData.last_name && !formData.lastName.trim()) {
        patch.lastName = userData.last_name;
      }

      const countryUntouched =
        (formData.country?.countryCode ?? null) === initialCountryCode.current;
      if (userData.country && countryUntouched) {
        const userCountry = countries.find((c) => c.name === userData.country);
        if (userCountry) {
          patch.country = userCountry;
        }
      }
    }

    if (!hasPrefilledSpecialty.current && !formData.specialty && specialtiesData) {
      if (userData.specialty_id) {
        const userSpecialty = specialtiesData.specialties.find(
          (s) => s.id === userData.specialty_id
        );
        if (userSpecialty) {
          patch.specialty = userSpecialty;
          hasPrefilledSpecialty.current = true;
        }
      } else if (userData.other_specialty) {
        patch.specialty = {
          id: OTHER_SPECIALTY_ID,
          name: 'Other',
          group: { id: 'other', name: 'Other' },
        };
        patch.otherSpecialty = userData.other_specialty;
        hasPrefilledSpecialty.current = true;
      }
    }

    if (Object.keys(patch).length > 0) {
      setFormData(patch);
    }
  }, [formData, userData, specialtiesData, setFormData]);
}
